// src/components/digital_twin/TerrainElevation3DLayer.js
// FlowSense MOW+ — TessaDEM ground elevation on the Digital Twin.
//   • One InstancedMesh: a stem under every asset with a ground elevation, height = metres above sea
//     level × exaggeration, coloured by hypsometric band (low-lying ground = blue/cyan).
//   • Wells in a terrain low (terrain_features.depression_m > 0.25) get a ring so flood-prone wells
//     stand out. Only assets with real TessaDEM data are drawn — nothing is invented.
//   • Rebuilt only when the asset set changes; toggled with `visible`.
// TerrainLegend renders the matching legend + coverage.
import React, { memo, useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { Box, HStack, VStack, Text, Badge, useColorModeValue } from '@chakra-ui/react';
import { buildProjector } from './SimulationImpact3DLayer';

export const ELEVATION_BANDS = Object.freeze([
  { max: 1, label: '≤ 1 m', hex: 0x2b6cb0, css: '#2B6CB0' },
  { max: 2, label: '1–2 m', hex: 0x00b5d8, css: '#00B5D8' },
  { max: 4, label: '2–4 m', hex: 0x38a169, css: '#38A169' },
  { max: 8, label: '4–8 m', hex: 0xd69e2e, css: '#D69E2E' },
  { max: Infinity, label: '> 8 m', hex: 0x9c4221, css: '#9C4221' },
]);
const bandOf = (m) => ELEVATION_BANDS.find((b) => m <= b.max) || ELEVATION_BANDS[ELEVATION_BANDS.length - 1];
const MAX_STEMS = 12000;
const EXAGGERATION = 8;

const parse = (raw) => { if (!raw) return null; if (typeof raw === 'object') return raw; try { return JSON.parse(raw); } catch (_) { return null; } };
const groundOf = (a) => { const g = Number(a?.ground_elevation_m); return a?.ground_elevation_m != null && Number.isFinite(g) ? g : null; };

/** Coverage stats for the legend (memo in the caller). */
export function terrainStats(assets = []) {
  let n = 0; let low = 0; let min = Infinity; let max = -Infinity; let sinks = 0;
  for (const a of assets) {
    const g = groundOf(a);
    if (g == null) continue;
    n += 1; if (g <= 2) low += 1; if (g < min) min = g; if (g > max) max = g;
    const t = parse(a.terrain_features);
    if (t && Number(t.depression_m) > 0.25) sinks += 1;
  }
  return { withElevation: n, total: assets.length, lowLying: low, sinks, min: n ? min : null, max: n ? max : null, coveragePct: assets.length ? Math.round((n / assets.length) * 100) : 0 };
}

const TerrainElevation3DLayer = ({ scene, origin, map, assets = [], visible = false }) => {
  const groupRef = useRef(null);
  const sig = useMemo(() => {
    let n = 0; let s = 0;
    for (const a of assets) { const g = groundOf(a); if (g != null) { n += 1; s += g; } }
    return `${assets.length}:${n}:${Math.round(s)}`;
  }, [assets]);
  const assetsRef = useRef(assets); assetsRef.current = assets;

  useEffect(() => {
    if (!scene) return undefined;
    let group = groupRef.current;
    if (!group) { group = new THREE.Group(); group.name = 'terrainElevation'; group.renderOrder = 800; scene.add(group); groupRef.current = group; }
    group.traverse((o) => { o.geometry?.dispose?.(); o.material?.dispose?.(); });
    while (group.children.length) group.remove(group.children[0]);

    const list = assetsRef.current;
    const byId = new Map(list.map((a) => [String(a.id), a]));
    const proj = buildProjector(scene, origin, byId);
    const m2s = proj.metersToScene;
    const items = [];
    for (const a of list) {
      if (items.length >= MAX_STEMS) break;
      const g = groundOf(a); if (g == null) continue;
      const pos = proj.objectById.get(String(a.id))?.position || proj.toScene(Number(a.latitude), Number(a.longitude));
      if (!pos) continue;
      const t = parse(a.terrain_features);
      items.push({ pos, g, sink: !!(t && Number(t.depression_m) > 0.25) });
    }
    if (items.length) {
      const geom = new THREE.CylinderGeometry(1, 1, 1, 6, 1); geom.rotateX(Math.PI / 2);
      const mat = new THREE.MeshStandardMaterial({ transparent: true, opacity: 0.85, roughness: 0.6, emissiveIntensity: 0.2 });
      const stems = new THREE.InstancedMesh(geom, mat, items.length);
      stems.name = 'terrain-stems';
      const dummy = new THREE.Object3D(); const c = new THREE.Color();
      items.forEach((it, i) => {
        const h = Math.max(0.3, it.g) * EXAGGERATION * m2s;
        dummy.position.set(it.pos.x, it.pos.y, proj.baseZ + h / 2);
        dummy.scale.set(3 * m2s, 3 * m2s, h); dummy.updateMatrix();
        stems.setMatrixAt(i, dummy.matrix);
        stems.setColorAt(i, c.setHex(bandOf(it.g).hex));
      });
      stems.instanceMatrix.needsUpdate = true; if (stems.instanceColor) stems.instanceColor.needsUpdate = true;
      group.add(stems);

      const sinks = items.filter((it) => it.sink);
      if (sinks.length) {
        const rg = new THREE.RingGeometry(10 * m2s, 14 * m2s, 24);
        const rm = new THREE.MeshBasicMaterial({ color: 0x00b5d8, transparent: true, opacity: 0.7, side: THREE.DoubleSide, depthWrite: false });
        const rings = new THREE.InstancedMesh(rg, rm, sinks.length);
        rings.name = 'terrain-sinks';
        sinks.forEach((it, i) => { dummy.position.set(it.pos.x, it.pos.y, proj.baseZ + 0.5 * m2s); dummy.scale.set(1, 1, 1); dummy.updateMatrix(); rings.setMatrixAt(i, dummy.matrix); });
        rings.instanceMatrix.needsUpdate = true;
        group.add(rings);
      }
    }
    group.visible = !!visible;
    map?.triggerRepaint?.();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, origin?.x, origin?.y, sig, map]);

  useEffect(() => { if (groupRef.current) { groupRef.current.visible = !!visible; map?.triggerRepaint?.(); } }, [visible, map]);
  useEffect(() => () => {
    const g = groupRef.current;
    if (g) { g.traverse((o) => { o.geometry?.dispose?.(); o.material?.dispose?.(); }); g.parent?.remove(g); }
    groupRef.current = null;
  }, []);
  return null;
};

export const TerrainLegend = memo(({ stats, visible }) => {
  const bg = useColorModeValue('rgba(255,255,255,0.92)', 'rgba(26,32,44,0.92)');
  const muted = useColorModeValue('gray.500', 'gray.400');
  if (!visible || !stats) return null;
  return (
    <Box position="absolute" top="72px" right="16px" zIndex={11} bg={bg} backdropFilter="blur(10px)" p={3} borderRadius="xl" boxShadow="lg" borderWidth="1px" borderColor="cyan.200" minW="190px">
      <HStack justify="space-between" mb={1.5}>
        <Text fontSize="xs" fontWeight="900">Ground elevation</Text>
        <Badge colorScheme="teal" fontSize="8px">TessaDEM</Badge>
      </HStack>
      <VStack align="stretch" spacing={1}>
        {ELEVATION_BANDS.map((b) => (
          <HStack key={b.label} spacing={2}><Box w="10px" h="10px" borderRadius="sm" bg={b.css} /><Text fontSize="10px">{b.label}</Text></HStack>
        ))}
        <HStack spacing={2}><Box w="10px" h="10px" borderRadius="full" borderWidth="2px" borderColor="cyan.400" /><Text fontSize="10px">Terrain low (run-off collects)</Text></HStack>
      </VStack>
      <Text fontSize="9px" color={muted} mt={2}>
        {stats.withElevation.toLocaleString()} of {stats.total.toLocaleString()} assets ({stats.coveragePct}%) · {stats.lowLying.toLocaleString()} ≤ 2 m · {stats.sinks} lows
        {stats.min != null ? ` · ${stats.min.toFixed(1)}–${stats.max.toFixed(1)} m` : ''}
      </Text>
      {stats.coveragePct < 50 && <Text fontSize="9px" color="orange.400" mt={1}>Run the elevation backfill for full coverage.</Text>}
    </Box>
  );
});
TerrainLegend.displayName = 'TerrainLegend';

export default TerrainElevation3DLayer;
